// Match the two rails at each direction guide, then interpolate along each
// rail between guides. This is a design preview, not export corner treatment.
const distance = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);
const cross = (a, b) => a.x * b.y - a.y * b.x;
const subtract = (a, b) => ({ x: a.x - b.x, y: a.y - b.y });

function measured(points) {
  const lengths = [0];
  for (let i = 1; i < points.length; i++) lengths.push(lengths[i - 1] + distance(points[i - 1], points[i]));
  return { points, lengths, total: lengths[lengths.length - 1] };
}

function at(rail, length) {
  let i = 1;
  while (i < rail.lengths.length - 1 && rail.lengths[i] < length) i++;
  const a = rail.points[i - 1], b = rail.points[i];
  const fraction = Math.max(0, Math.min(1, (length - rail.lengths[i - 1]) / (rail.lengths[i] - rail.lengths[i - 1] || 1)));
  return { x: a.x + (b.x - a.x) * fraction, y: a.y + (b.y - a.y) * fraction };
}

function intersection(rail, guide) {
  const hits = [];
  for (let j = 1; j < guide.length; j++) {
    const q = guide[j - 1], s = subtract(guide[j], q);
    for (let i = 1; i < rail.points.length; i++) {
      const p = rail.points[i - 1], r = subtract(rail.points[i], p);
      const denominator = cross(r, s);
      if (Math.abs(denominator) < 1e-10) continue;
      const qp = subtract(q, p);
      const t = cross(qp, s) / denominator, u = cross(qp, r) / denominator;
      if (t >= -1e-7 && t <= 1 + 1e-7 && u >= -1e-7 && u <= 1 + 1e-7) {
        const hit = rail.lengths[i - 1] + Math.max(0, Math.min(1, t)) * distance(p, rail.points[i]);
        if (!hits.some((value) => Math.abs(value - hit) < 1e-6)) hits.push(hit);
      }
    }
  }
  // Guides ending exactly on a curved SVG rail can miss its sampled chords.
  // Snap only within a small fraction of one sample segment.
  if (!hits.length && guide.length) {
    let closest = null;
    for (const point of [guide[0], guide[guide.length - 1]]) {
      for (let i = 1; i < rail.points.length; i++) {
        const p = rail.points[i - 1], q = rail.points[i], v = subtract(q, p);
        const squared = v.x * v.x + v.y * v.y;
        if (!squared) continue;
        const t = Math.max(0, Math.min(1, ((point.x - p.x) * v.x + (point.y - p.y) * v.y) / squared));
        const error = distance(point, { x: p.x + t * v.x, y: p.y + t * v.y });
        if (error <= Math.max(1e-6, Math.sqrt(squared) * 0.05) && (!closest || error < closest.error)) {
          closest = { error, length: rail.lengths[i - 1] + t * Math.sqrt(squared) };
        }
      }
    }
    if (closest) hits.push(closest.length);
  }
  // A guide crossing a rail twice has no unambiguous correspondence.
  return hits.length === 1 ? hits[0] : null;
}

export function guidedSatinSegments(left, right, guides, spacing, pull = 0) {
  if (left.length < 2 || right.length < 2 || !(spacing > 0)) return [];
  const closed = (points) => distance(points[0], points[points.length - 1]) < 1e-5;
  const loop = closed(left) && closed(right);
  const winding = (points) => points.reduce((area, p, i) => i ? area + cross(points[i - 1], p) : area, 0);
  const orientedRight = loop && winding(left) * winding(right) < 0 ? [...right].reverse() : right;
  const a = measured(left), b = measured(orientedRight);
  if (!a.total || !b.total) return [];
  let candidates = guides.map((guide) => [intersection(a, guide), intersection(b, guide)])
    .filter(([x, y]) => x !== null && y !== null);
  // Closed rails have arbitrary SVG start points. Anchor both loops at the
  // same physical guide before sorting; otherwise one interval can span most
  // of one rail and only a tiny portion of the other, creating a giant fan.
  let origin = [0, 0];
  if (loop && candidates.length) {
    origin = candidates.reduce((best, value) => value[0] < best[0] ? value : best);
    candidates = candidates.map(([x, y]) => [(x - origin[0] + a.total) % a.total, (y - origin[1] + b.total) % b.total]);
  }
  candidates.sort((p, q) => p[0] - q[0]);
  const anchors = [[0, 0]];
  for (const candidate of candidates) {
    const previous = anchors[anchors.length - 1];
    if (candidate[0] > previous[0] + 1e-6 && candidate[1] > previous[1] + 1e-6 &&
        candidate[0] < a.total - 1e-6 && candidate[1] < b.total - 1e-6) anchors.push(candidate);
  }
  anchors.push([a.total, b.total]);
  const result = [];
  for (let section = 1; section < anchors.length; section++) {
    const start = anchors[section - 1], end = anchors[section];
    const count = Math.max(1, Math.ceil(((end[0] - start[0]) + (end[1] - start[1])) / (2 * spacing)));
    for (let i = 0; i < count + (section === anchors.length - 1 ? 1 : 0); i++) {
      const t = i / count;
      const positionA = start[0] + (end[0] - start[0]) * t;
      const positionB = start[1] + (end[1] - start[1]) * t;
      const p = at(a, loop ? (positionA + origin[0]) % a.total : positionA);
      const q = at(b, loop ? (positionB + origin[1]) % b.total : positionB);
      const length = distance(p, q);
      if (length && pull > 0) {
        const dx = (q.x - p.x) / length * pull, dy = (q.y - p.y) / length * pull;
        p.x -= dx; p.y -= dy; q.x += dx; q.y += dy;
      }
      result.push([p, q]);
    }
  }
  return result;
}
